// ✅ src/pages/orders/modals/DispatchConfirmModal.jsx
import { useEffect, useState } from "react";
import {
  X,
  Send,
  User,
  FileText,
  AlertTriangle,
  Eye,
  EyeOff,
  RotateCcw,
} from "lucide-react";

import HtmlPreview from "../../../../utils/HtmlPreview";
import { resultsTableHtml } from "../../../../utils/resultsTableHtml";

/* --------------------------------------------------
   Fetch order tests for the confirm modal
-------------------------------------------------- */
const fetchOrderTests = async (orderId) => {
  try {
    const res = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}/api/orders/${orderId}/tests`,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );

    const data = await res.json();
    return data?.success ? data.orderMembers || [] : [];
  } catch (e) {
    console.error("fetchOrderTests error:", e);
    return [];
  }
};

/* --------------------------------------------------
   ✅ Fetch preview results — returns patientReports[]
   API: /api/results/orders/:orderId
-------------------------------------------------- */
const fetchResultsPreview = async (orderId) => {
  try {
    const res = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}/api/results/orders/${orderId}`,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    const data = await res.json();

    const patientReports = data?.data?.patientReports || [];
    const layout = data?.data?.layout || null;

    return { patientReports, layout };
  } catch (e) {
    console.error("fetchResultsPreview error:", e);
    return { patientReports: [], layout: null };
  }
};

/* --------------------------------------------------
   Small badge helpers
-------------------------------------------------- */
/* --------------------------------------------------
   ✅ Convert trend map safely
-------------------------------------------------- */
const toTrendMap = (trendsObj) => {
  try {
    if (!trendsObj) return null;

    // already Map
    if (trendsObj instanceof Map) return trendsObj;

    // plain object -> Map
    if (typeof trendsObj === "object") {
      return new Map(
        Object.entries(trendsObj).map(([k, v]) => [k, v])
      );
    }

    return null;
  } catch (e) {
    console.error("toTrendMap error:", e);
    return null;
  }
};

/* --------------------------------------------------
   Main Modal
-------------------------------------------------- */
const DispatchConfirmModal = ({ isOpen, onClose, onConfirm, orderId, order, pendingCount = 0, totalCount = 0 }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [pendingAcknowledged, setPendingAcknowledged] = useState(false);

  const hasPendingResults = pendingCount > 0;

  // ✅ Preview state — per-patient array
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPatients, setPreviewPatients] = useState([]); // [{ patient, html, error }]
  const [previewError, setPreviewError] = useState("");
  const [showPreview, setShowPreview] = useState(true);

  /* -----------------------------
     Load member/tests list
  ------------------------------ */
  useEffect(() => {
    if (!isOpen || !orderId) return;

    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const list = await fetchOrderTests(orderId);
        if (mounted) setMembers(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error("Load members error:", e);
        if (mounted) setMembers([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [isOpen, orderId]);

  /* -----------------------------
     Load preview (per patient)
  ------------------------------ */
  const loadPreview = async () => {
    if (!orderId) return;

    setPreviewLoading(true);
    setPreviewError("");
    setPreviewPatients([]);

    try {
      const { patientReports, layout } = await fetchResultsPreview(orderId);

      if (!Array.isArray(patientReports) || patientReports.length === 0) {
        setPreviewLoading(false);
        return;
      }

      // ✅ ONLY show patients who have at least 1 result
      const rendered = patientReports
        .filter((pr) => (pr?.results || []).length > 0)
        .map((pr) => {
          try {
            // ✅ Attach reportItems to each result
            const resultsWithItems = pr.results.map((r) => ({
              ...r,
              reportItems: pr.reportItemsByTestId
                ? pr.reportItemsByTestId[r.testId] || []
                : [],
            }));

            // ✅ No trends returned from getOrderReportsAllPatients currently
            const trendMap = null; 

            const html = resultsTableHtml({
              results: resultsWithItems,
              trendMap,
              layout,
              returnMeta: false,
            });

            return {
              patient: pr.patient,
              html: String(html || ""),
              error: null,
            };
          } catch (renderErr) {
            console.error(
              `resultsTableHtml error for patient ${pr?.patient?.fullName}:`,
              renderErr
            );
            return {
              patient: pr.patient,
              html: "",
              error:
                renderErr?.message ||
                "Failed to render this patient's preview.",
            };
          }
        });

      setPreviewPatients(rendered);
    } catch (e) {
      console.error("loadPreview fetch error:", e);
      setPreviewError(`Failed to load preview: ${e?.message || e}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    setPreviewPatients([]);
    setPreviewError("");
    setShowPreview(true);
    setPendingAcknowledged(false);

    loadPreview().catch((e) => console.error("Auto loadPreview error:", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, orderId]);

  const handleConfirm = async () => {
    try {
      setConfirming(true);
      await onConfirm();
    } catch (e) {
      console.error("dispatch confirm error:", e);
      alert("Failed to confirm dispatch. Please try again.");
    } finally {
      setConfirming(false);
    }
  };

  if (!isOpen) return null;

  /* flatten all tests for quick summary count */
  let totalTests = 0;
  try {
    members.forEach((m) => {
      (m.orderMemberPackages || []).forEach((item) => {
        if (item.test) totalTests += 1;
        if (item.package?.checkupPackages?.length)
          totalTests += item.package.checkupPackages.length;
      });
    });
  } catch (e) {
    console.error("Total tests calc error:", e);
  }

  return (
  <div className="fixed inset-0 z-[99999] isolate flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-5xl max-h-[90vh] flex flex-col rounded-2xl bg-white shadow-2xl ring-1 ring-black/10">
        {/* ── Header ── */}
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-gray-200 shrink-0">
          <div>
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <Send className="w-4 h-4 text-green-600" />
              Confirm Report Dispatch
            </h3>
            <p className="mt-0.5 text-sm text-gray-500">
              Review all test results below before dispatching.
            </p>
          </div>

          <button
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── Order summary strip ── */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 shrink-0 flex flex-wrap gap-4 text-xs text-gray-600">
          <span>
            <span className="font-medium text-gray-800">Order:</span>{" "}
            {order?.orderNumber || `#${orderId}`}
          </span>
          <span>
            <span className="font-medium text-gray-800">Patient:</span>{" "}
            {order?.patient?.fullName || "—"}
          </span>
          <span>
            <span className="font-medium text-gray-800">Center:</span>{" "}
            {order?.diagnosticCenter?.name || order?.center?.name || "—"}
          </span>
          <span>
            <span className="font-medium text-gray-800">Total Tests:</span>{" "}
            {loading ? "…" : totalTests}
          </span>
        </div>

        {/* ── Body (scrollable) ── */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-5">
          {/* ── Loading state ── */}
          {loading && (
            <div className="flex items-center justify-center py-12 text-sm text-gray-400">
              Loading test details…
            </div>
          )}

          {/* ── Empty state ── */}
          {!loading && members.length === 0 && (
            <div className="flex items-center justify-center py-12 text-sm text-gray-400">
              No tests found for this order.
            </div>
          )}

      
          

          {/* ── Pending Results Warning ── */}
          {hasPendingResults && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 space-y-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-800">
                    {pendingCount} of {totalCount} test{totalCount !== 1 ? "s" : ""} have no result entered
                  </p>
                  <p className="text-xs text-red-600 mt-0.5">
                    The dispatched report will not include results for these tests. This cannot be undone without regenerating the report.
                  </p>
                </div>
              </div>

              {/* List pending tests from loaded members */}
              {!loading && members.length > 0 && (() => {
                const pendingTests = [];
                members.forEach((m) => {
                  (m.orderMemberPackages || []).forEach((item) => {
                    if (item.test) {
                      const st = String(item.test?.result?.status || "").toUpperCase();
                      const done = st && st !== "DRAFT" && st !== "PENDING";
                      if (!done) pendingTests.push({ name: item.test.name, patient: m.patient?.fullName });
                    }
                    (item.package?.checkupPackages || []).forEach((cp) => {
                      if (cp.test) {
                        const st = String(cp.test?.result?.status || "").toUpperCase();
                        const done = st && st !== "DRAFT" && st !== "PENDING";
                        if (!done) pendingTests.push({ name: cp.test.name, patient: m.patient?.fullName });
                      }
                    });
                  });
                });
                return pendingTests.length > 0 ? (
                  <ul className="text-xs text-red-700 space-y-1 pl-7 list-disc">
                    {pendingTests.map((t, i) => (
                      <li key={i}>
                        <span className="font-medium">{t.name}</span>
                        {t.patient && <span className="text-red-500"> — {t.patient}</span>}
                      </li>
                    ))}
                  </ul>
                ) : null;
              })()}

              <label className="flex items-center gap-2 cursor-pointer pl-1">
                <input
                  type="checkbox"
                  checked={pendingAcknowledged}
                  onChange={(e) => setPendingAcknowledged(e.target.checked)}
                  className="w-4 h-4 accent-red-600"
                />
                <span className="text-xs font-medium text-red-800">
                  I understand some tests have no results — proceed with dispatch anyway
                </span>
              </label>
            </div>
          )}

          {/* ── ✅ Per-Patient Result Preview ── */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            {/* Preview header */}
            <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-100">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-700" />
                <p className="text-sm font-semibold text-gray-900">
                  Test Result Preview
                </p>
                {previewLoading && (
                  <span className="text-[11px] text-gray-500">Loading…</span>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowPreview((s) => !s)}
                  className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
                >
                  {showPreview ? (
                    <>
                      <EyeOff className="w-4 h-4" /> Hide
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4" /> Show
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => loadPreview().catch(() => {})}
                  className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
                >
                  <RotateCcw className="w-4 h-4" />
                  Refresh
                </button>
              </div>
            </div>

            {/* Preview body */}
            {showPreview && (
              <div className="p-3 bg-gray-50 space-y-4">
                {previewError ? (
                  <div className="text-xs text-red-600">{previewError}</div>
                ) : previewLoading ? (
                  <div className="text-xs text-gray-500 py-6 text-center">
                    Fetching preview…
                  </div>
                ) : previewPatients.length > 0 ? (
                  previewPatients.map((pp, i) => (
                    <div
                      key={pp.patient?.id ?? i}
                      className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                      {/* Per-patient header */}
                      <div className="px-3 py-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
                        <User className="w-3.5 h-3.5 text-blue-600" />
                        <span className="text-xs font-semibold text-blue-800">
                          {pp.patient?.fullName || `Patient ${i + 1}`}
                        </span>
                        {pp.patient?.age && (
                          <span className="text-[10px] text-blue-500">
                            {pp.patient.age} yrs
                          </span>
                        )}
                        {pp.patient?.gender && (
                          <span className="text-[10px] text-blue-400 uppercase">
                            · {pp.patient.gender}
                          </span>
                        )}
                      </div>

                      {/* Per-patient HTML preview OR error */}
                      {pp.error ? (
                        <div className="px-3 py-4 bg-red-50 text-xs text-red-600 flex items-start gap-2">
                          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                          <span>
                            <span className="font-semibold">Render error: </span>
                            {pp.error}
                            <span className="block mt-1 text-red-400">
                              Check browser console for full details.
                            </span>
                          </span>
                        </div>
                      ) : (
                        <div className="max-h-[280px] overflow-auto bg-white p-3">
                          <HtmlPreview html={pp.html} />
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-xs text-gray-500 py-6 text-center">
                    No results available for preview.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            This action will mark the report as dispatched and cannot be undone
            without regenerating.
          </div>

          <div className="flex gap-2 shrink-0">
            <button
              onClick={onClose}
              disabled={confirming}
              className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              onClick={handleConfirm}
              disabled={confirming || loading || (hasPendingResults && !pendingAcknowledged)}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
              {confirming ? "Dispatching…" : "Confirm Dispatch"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DispatchConfirmModal;